/**
 * Following Tab Content Component
 * Displays paginated list of users that the profile owner is following
 */

import { useQuery } from "@tanstack/react-query"
import { Loader2, Search, User, X } from "lucide-react"
import { useRef, useState } from "react"
import { BlurGate } from "@/components/guest"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"
import { getFollowing } from "../services/follow.service"
import { FollowerCard } from "./FollowerCard"

interface FollowingTabContentProps {
  userId: string
  searchQuery?: string
  showHeader?: boolean
  headerTitle?: string
  isGuest?: boolean
}

export function FollowingTabContent({
  userId,
  searchQuery,
  showHeader: _showHeader = true,
  headerTitle,
  isGuest = false,
}: FollowingTabContentProps) {
  useTranslation("profile")
  const [showSearch, setShowSearch] = useState(false)
  const [localSearch, setLocalSearch] = useState("")
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [page, setPage] = useState(1)
  const currentUser = useAuthStore(state => state.user)
  const debouncedSearch = useDebounce(localSearch || searchQuery || "", 500)

  const prevDebouncedSearch = useRef(debouncedSearch)
  if (prevDebouncedSearch.current !== debouncedSearch) {
    prevDebouncedSearch.current = debouncedSearch
    setPage(1)
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ["following", userId, page, debouncedSearch],
    queryFn: () => getFollowing(userId, page, undefined, debouncedSearch),
    enabled: !!userId,
  })

  const following = data?.following || []
  const pages = data?.pages ?? 1

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-red">Error loading following</p>
      </div>
    )
  }

  if (following.length === 0) {
    return (
      <div className="text-center py-12 text-stone">
        <User className="mx-auto mb-2 h-12 w-12 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Not following anyone yet</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {headerTitle ? (
        <div className="flex items-center justify-between px-5 md:px-0">
          <h2 className="text-lg font-semibold">{headerTitle}</h2>
          <div className="flex justify-end lg:hidden">
            {showSearch ? (
              <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 h-7.5">
                <Search className="w-4 h-4 text-primary/50 shrink-0" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={localSearch}
                  onChange={e => setLocalSearch(e.target.value)}
                  className="max-w-48 border-none h-7.5 bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                  autoFocus
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-6 w-6 text-primary/50"
                  onClick={() => {
                    setShowSearch(false)
                    setLocalSearch("")
                  }}
                  aria-label="Close search"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                className="opacity-50 cursor-pointer h-5"
                onClick={() => (isGuest ? setShowLoginModal(true) : setShowSearch(true))}
                aria-label="Search"
              >
                <Search className="w-5 h-5 text-primary" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex justify-end md:hidden">
          {showSearch ? (
            <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 h-7.5">
              <Search className="w-4 h-4 text-primary/50 shrink-0" />
              <Input
                type="text"
                placeholder="Search..."
                value={localSearch}
                onChange={e => setLocalSearch(e.target.value)}
                className="max-w-48 border-none h-7.5 bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                autoFocus
              />
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 h-6 w-6 text-primary/50"
                onClick={() => {
                  setShowSearch(false)
                  setLocalSearch("")
                }}
                aria-label="Close search"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <button
              type="button"
              className="opacity-50 cursor-pointer h-5"
              onClick={() => (isGuest ? setShowLoginModal(true) : setShowSearch(true))}
              aria-label="Search"
            >
              <Search className="w-5 h-5 text-primary" />
            </button>
          )}
        </div>
      )}
      {isGuest ? (
        <BlurGate ctaVariant="followers">
          <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 gap-5 my-6">
            {following.map(item => (
              <FollowerCard key={item.id} item={item} currentUserId={currentUser?.id} />
            ))}
          </div>
        </BlurGate>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 gap-5 my-6">
          {following.map(item => (
            <FollowerCard key={item.id} item={item} currentUserId={currentUser?.id} />
          ))}
        </div>
      )}

      {pages > 1 && !isGuest && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p - 1)}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-stone px-2">
            {page} of {pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page === pages}
          >
            Next
          </Button>
        </div>
      )}
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
